import React from 'react';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { HyperlinkAddToolbar } from '@atlaskit/editor-common/link/HyperlinkAddToolbar';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import type { Command } from '@atlaskit/editor-common/types/command';
import type { FloatingToolbarItem } from '@atlaskit/editor-common/types/floating-toolbar';
import type { LinkPickerOptions } from '@atlaskit/editor-common/types/hyperlink';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { ContentNodeWithPos } from '@atlaskit/editor-prosemirror/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { getParameters, setParameter } from '@atlaskit/native-embeds-common/utils';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core/services/main';

import type { EditorPluginNativeEmbedsPlugin } from '../../nativeEmbedsPluginType';
import { createConvertNativeEmbedToEmbedCardCommand, hideUrlToolbar } from '../commands/url';

interface EditUrlToolbarProps {
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined;
	linkPickerOptions?: LinkPickerOptions;
	lpLinkPicker: boolean;
	providerFactory: ProviderFactory;
	selectedNativeEmbed: ContentNodeWithPos;
	view: EditorView;
}

export const EditUrlToolbar = ({
	api,
	linkPickerOptions,
	lpLinkPicker,
	providerFactory,
	selectedNativeEmbed,
	view,
}: EditUrlToolbarProps): React.JSX.Element => {
	const currentUrl = getParameters(selectedNativeEmbed.node.attrs.parameters, 'url');
	const currentDisplayText = getParameters(
		selectedNativeEmbed.node.attrs.parameters,
		'displayText',
	);
	const localId = selectedNativeEmbed.node.attrs.localId as string | undefined;

	const onSubmit = (href: string, _title: string | undefined, displayText: string | undefined) => {
		hideUrlToolbar(view.state, view.dispatch);

		const urlChanged = href !== currentUrl;
		const displayTextChanged = displayText !== currentDisplayText;

		if (urlChanged) {
			// If the new URL is no longer supported by any native embed experience,
			// convert the native embed extension back to a standard embed card.
			const isNativeEmbedUrl = Boolean(getNativeEmbedFacade().getManifestByUrl(href));
			if (!isNativeEmbedUrl) {
				createConvertNativeEmbedToEmbedCardCommand(href)(view.state, view.dispatch);
				return;
			}
		}

		if ((urlChanged || displayTextChanged) && localId) {
			const extensionApi = api?.extension?.actions?.api();
			if (extensionApi?.doc?.update) {
				extensionApi.doc.update(localId, (current) => ({
					attrs: {
						...current.attrs,
						parameters: setParameter(current.attrs?.parameters, {
							url: href,
							displayText,
						}),
					},
				}));
			}
		}
	};

	return (
		<HyperlinkAddToolbar
			view={view}
			providerFactory={providerFactory}
			linkPickerOptions={linkPickerOptions}
			displayUrl={currentUrl}
			displayText={currentDisplayText}
			lpLinkPicker={lpLinkPicker}
			invokeMethod={INPUT_METHOD.FLOATING_TB}
			/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
			onSubmit={(href, title, displayText) => onSubmit(href, title, displayText)}
			onEscapeCallback={hideUrlToolbar}
			onClickAwayCallback={hideUrlToolbar}
		/>
	);
};

export const buildNativeEmbedEditUrlToolbar = (
	props: Omit<EditUrlToolbarProps, 'view'>,
): FloatingToolbarItem<Command> => ({
	type: 'custom',
	disableArrowNavigation: true,
	fallback: [],
	render: (view) =>
		view ? (
			<EditUrlToolbar
				api={props.api}
				linkPickerOptions={props.linkPickerOptions}
				lpLinkPicker={props.lpLinkPicker}
				providerFactory={props.providerFactory}
				selectedNativeEmbed={props.selectedNativeEmbed}
				view={view}
			/>
		) : null,
});
